import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RawDocumentInterface } from '../interfaces/raw-document.interface';
import { Document } from '../models/document.model';
import { DocumentVisaInterface } from '../interfaces/document-visa.interface';
import { DocumentVisa } from '../models/dcoument-visa.model';
import { VisaData } from '../interfaces/visa-data.interface';
import { DocumentValidation } from '../models/document-validation.model';
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
import { DocumentTypesEnum } from '../enums/document-types.enum';
import { Params } from '@angular/router';
import { BydGenerateParams } from '@sixense/byd-core-service';
import { DsDocFiltersCheckModel } from '../models/ds-doc-filters-check.model';

@Injectable({ providedIn: 'root' })
export class DocumentsService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get all documents.
   *
   * @param projectId ProjectId.
   * @returns BydModels of Documents.
   */
  public getAllDocuments(projectId: number): Observable<Document[]> {
    this.path = `/v1/bimdata/projects/${projectId}/document`;
    return this.get<RawDocumentInterface[]>().pipe(map(this.sortDocuments));
  }

  /**
   * Get filtered docs list.
   *
   * @param projectId Project ID
   * @param numberPerPage Number of results per page
   * @param queries collection of matrix and query URL parameters
   * @param pageIndex Current page index of results
   * @returns Observable type `<BydModel<Document>>`
   */
  public getFilteredDocsList(
    projectId: number,
    queries: Params,
    numberPerPage: number,
    pageIndex: number
  ): Observable<Document[]> {
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(numberPerPage, pageIndex);
    const queryParams = new DsDocFiltersCheckModel(queries);
    const params = BydGenerateParams<DsDocFiltersCheckModel>(queryParams);

    this.path = `/v1/bimdata/projects/${projectId}/document`;
    return this.get<RawDocumentInterface[]>({ headers, params }).pipe(map(this.sortDocuments));
  }

  /**
   * Create new document.
   *
   * @param projectId ProjectId.
   * @param file File.
   * @param name File name.
   * @param parentId ParentId.
   * @param successorOf Successor of.
   * @returns created Document.
   */
  public uploadDocument(
    projectId: number,
    file: File,
    name: string,
    parentId?: number,
    successorOf?: number
  ): Observable<Document> {
    this.path = `/v1/bimdata/projects/${projectId}/document`;
    const formData = new FormData();
    formData.append('file', file, name);
    formData.append('name', name);
    if (parentId) formData.append('parent_id', '' + parentId);
    if (successorOf) formData.append('successor_of', '' + successorOf);
    const headers = this.bydConfigService.headers.delete('Content-Type');
    return this.post<RawDocumentInterface>({ body: formData, headers }).pipe(
      map((document: RawDocumentInterface) => new Document(document))
    );
  }

  /**
   * Delete documents by ID.
   *
   * @param projectId Project Id.
   * @param documentId Document id.
   * @returns Empty observable.
   */
  public deleteDocument(projectId: number, documentId: number): Observable<void> {
    this.path = `/v1/bimdata/projects/${projectId}/document/${documentId}`;
    return this.delete<void>();
  }

  /**
   * Rename Document.
   *
   * @param projectId project ID.
   * @param documentId document id.
   * @param name Name.
   * @returns Updated Document.
   */
  public renameDocument(projectId: number, documentId: number, name: string): Observable<Document> {
    const data = { name, file_name: name };
    this.path = `/v1/bimdata/projects/${projectId}/document/${documentId}`;
    return this.patch<RawDocumentInterface>({ body: data }).pipe(
      map((document: RawDocumentInterface) => new Document(document))
    );
  }

  /**
   * Add tag to a document.
   *
   * @param projectId project ID.
   * @param documentId document id.
   * @param tagId tag id.
   * @returns Updated Document.
   */
  public addTag(projectId: number, documentId: number, tagId: number): Observable<void> {
    const data = { tag_id: tagId };
    this.path = `/v1/bimdata/projects/${projectId}/document/${documentId}/tag`;
    return this.post<void>({ body: data });
  }

  /**
   * Remove tag from document.
   *
   * @param projectId project ID.
   * @param documentId document id.
   * @param tagId tag id.
   * @returns Updated Document.
   */
  public removeTag(projectId: number, documentId: number, tagId: number): Observable<void> {
    this.path = `/v1/bimdata/projects/${projectId}/document/${documentId}/tag/${tagId}`;
    return this.delete<void>();
  }

  /**
   * Get document visas.
   *
   * @param projectId ProjectId.
   * @param documentId DocumentId.
   * @returns visas.
   */
  public getDocumentVisas(projectId: number, documentId: number): Observable<DocumentVisa[]> {
    this.path = `/v1/bimdata/projects/${projectId}/document/${documentId}/visa`;
    return this.get<DocumentVisaInterface[]>().pipe(
      map((visas: DocumentVisaInterface[]) => visas.map((visa: DocumentVisaInterface) => new DocumentVisa(visa)))
    );
  }

  /**
   * Create new visa for document.
   *
   * @param projectId Project Id.
   * @param documentId DOcument Id.
   * @param data Data.
   * @returns Observable DocumentVisa
   */
  public createDocumentVisa(projectId: number, documentId: number, data: VisaData): Observable<DocumentVisa> {
    this.path = `/v1/bimdata/projects/${projectId}/document/${documentId}/visa`;
    return this.post<DocumentVisaInterface>({ body: data }).pipe(
      map((visa: DocumentVisaInterface) => new DocumentVisa(visa))
    );
  }

  /**
   * Add validation to visa.
   *
   * @param projectId Project Id.
   * @param visa visa data.
   * @param validatorId Validator Id.
   * @returns Observable DocumentVisa
   */
  public addValidationToVisa(
    projectId: number,
    visa: DocumentVisa,
    validatorId: number
  ): Observable<DocumentValidation> {
    const data = { validator_id: validatorId };
    this.path = `/v1/bimdata/projects/${projectId}/document/${visa.documentId}/visa/${visa.id}/validation`;
    return this.post<DocumentValidation>({ body: data });
  }

  /**
   * Get all versions.
   *
   * @param projectId Project Id.
   * @param documentId Document Id.
   * @returns Observable of Document.
   */
  public getDocumentHistory(projectId: number, documentId: number): Observable<Document[]> {
    this.path = `/v1/bimdata/projects/${projectId}/document/${documentId}/history`;
    return this.get<RawDocumentInterface[]>().pipe(
      map((documents: RawDocumentInterface[]) =>
        documents.map(
          (document: RawDocumentInterface) => new Document({ ...document, type: DocumentTypesEnum.DOCUMENT })
        )
      )
    );
  }

  /**
   * Update history head.
   *
   * @param projectId ProjectId.
   * @param documentId DocumentId.
   * @param historyId HistoryId.
   * @returns Observable of Document
   */
  public updateHeadOfHistory(projectId: number, documentId: number, historyId: number): Observable<Document> {
    this.path = `/v1/bimdata/projects/${projectId}/document/${documentId}/history/${historyId}/head-version`;
    return this.post<RawDocumentInterface>().pipe(map((document: RawDocumentInterface) => new Document(document)));
  }

  /**
   * Update Document parent.
   *
   * @param projectId project ID.
   * @param document Document updated.
   * @returns Updated Document.
   */
  public updateParent(projectId: number, document: Document): Observable<Document> {
    this.path = `/v1/bimdata/projects/${projectId}/document/${document.id}`;
    return this.patch<RawDocumentInterface>({ body: { parent_id: document.parentId } }).pipe(
      map((doc: RawDocumentInterface) => new Document(doc))
    );
  }

  /**
   * Create Document instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `Document` instance.
   */
  public createNewInstance(jsonObject: RawDocumentInterface): Document {
    return new Document(jsonObject);
  }

  /**
   * Sort documents.
   *
   * @param documents Raw documents.
   * @returns Document[]
   */
  private sortDocuments(documents: RawDocumentInterface[]): Document[] {
    return documents
      .map((document: RawDocumentInterface) => new Document(document))
      .sort((doc1: Document, doc2: Document) => doc2.createdAt.getTime() - doc1.createdAt.getTime());
  }
}
